import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Send, FileSignature, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "packet_opened", label: "Packet opened", icon: Check },
  { key: "sent", label: "Sent to NEXA", icon: Send },
  { key: "signed", label: "Signed", icon: FileSignature },
];
const RANK = { not_started: 0, packet_opened: 1, sent: 2, signed: 3 };

export const FilingStatusBar = ({ month, filing, authHeader, onChange, readOnly = false }) => {
  const [ref, setRef] = useState(filing?.pandadoc_ref || "");
  const [busy, setBusy] = useState("");
  const status = filing?.status || "not_started";
  const rank = RANK[status] ?? 0;

  const mark = async (next) => {
    setBusy(next);
    try {
      const { data } = await axios.post(
        `${API}/bdm/filing`,
        { month, status: next, pandadoc_ref: ref },
        { headers: authHeader() },
      );
      toast.success(next === "signed" ? "Filed and signed — nice work" : "Marked as sent");
      onChange?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update that.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-linen-border p-5" data-testid="bdm-filing-bar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-teal">This month's filing</p>
          <p className="mt-0.5 text-sm text-ink-500">
            {status === "signed"
              ? "Done. Nothing else owed for this month."
              : status === "sent"
              ? "Sent — mark it signed once the signature is in."
              : "Copy the wording below into the form, then track it here."}
          </p>
        </div>
        <div className="flex items-center gap-1.5" data-testid="bdm-filing-steps">
          {STEPS.map((s, i) => {
            const reached = rank >= RANK[s.key];
            const Icon = s.icon;
            return (
              <div key={s.key} className="flex items-center gap-1.5">
                {i > 0 && <span className={`w-5 h-px ${reached ? "bg-teal" : "bg-linen-border"}`} />}
                <span
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-1 rounded-md ${
                    reached ? "bg-teal/15 text-teal" : "bg-linen text-ink-400"
                  }`}
                >
                  <Icon className="w-3 h-3" /> {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {!readOnly && status !== "signed" && (
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="block">
            <span className="text-[11px] uppercase tracking-widest text-ink-500">
              PandaDoc reference <span className="normal-case tracking-normal text-ink-400">(optional)</span>
            </span>
            <input
              value={ref}
              onChange={(e) => setRef(e.target.value)}
              placeholder="e.g. BDM-0925-014"
              data-testid="bdm-filing-ref"
              className="mt-1 w-56 rounded-xl border border-linen-border p-2 text-sm text-navy bg-linen/40 focus:bg-white focus:border-teal focus:outline-none"
            />
          </label>
          {rank < RANK.sent && (
            <button
              type="button"
              onClick={() => mark("sent")}
              disabled={!!busy}
              data-testid="bdm-filing-mark-sent"
              className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
            >
              {busy === "sent" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              I've sent it to NEXA
            </button>
          )}
          <button
            type="button"
            onClick={() => mark("signed")}
            disabled={!!busy}
            data-testid="bdm-filing-mark-signed"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-full bg-lime text-navy hover:brightness-95 disabled:opacity-60"
          >
            {busy === "signed" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSignature className="w-3.5 h-3.5" />}
            It's signed
          </button>
        </div>
      )}

      {status === "signed" && filing?.pandadoc_ref ? (
        <p className="mt-3 text-xs text-ink-500">Reference: <span className="font-mono">{filing.pandadoc_ref}</span></p>
      ) : null}
    </div>
  );
};

export default FilingStatusBar;
